/* eslint-disable react/prop-types, no-underscore-dangle */
import React from 'react';

import { Header, Grid, Breadcrumb, Button, Label, Card, List } from 'semantic-ui-react';

import { graphql } from 'gatsby';
import _head from 'lodash/head';
import _isEmpty from 'lodash/isEmpty';

import {
  Image,
  InternalLink,
  MarkdownContent,
  SEO,
  Price,
  OutboundLink,
  CatalogItemShareButton,
  FullImageCard,
  FishermanIcon,
  CatalogItemLikeButton,
  ServiceItemSchema,
  BreadcrumbListSchema,
  WebsiteComponentInteraction,
  BasicCarousel,
  componentIterator,
  EVENT_CATEGORIES,
  EVENT_ACTIONS,
  ModifierSetsAccordions,
  FadeReveal,
  Section,
  ScrollableContent,
} from '@bluefin/components';

import ItemLocationAvailability from '../../components/ItemLocationAvailability/ItemLocationAvailability';
import ServiceItemAttributeList from '../../components/ServiceItemAttributeList';
import ServiceItemDuration from '../../components/ServiceItemDuration';
import TeamMemberLinkList from '../../components/TeamMemberLinkList';
import { createServiceCategorySlug, createServiceItemSlug } from '../../index';
import {
  getBookNowPage,
  getBusinessFiles,
  getFirstBusinessFile,
  getCtaInteractions,
  getServiceItemModifierSets,
  getOtherCategoryItems,
  getComponentContentNodeContent,
  getServiceTeamMembers,
  createVariationShortDescription,
} from '../../template-utils';
import { constructFullPageUrl, ensureSlugSlashes, getPageUrl } from '../../utils';

export default function ServiceItemTemplate({ data, pageContext }) {
  const {
    fishermanBusinessMenuItem,
    fishermanBusiness: business = {},
    allFishermanBusinessMenuCategory: { nodes: categories },
    allFishermanBusinessMenuItem: { nodes: allItems },
    allFishermanBusinessMenuModifierSet: { nodes: allModifierSets },
    allFishermanBusinessMenuModifier: { nodes: allModifiers },
    allFishermanBusinessWebsitePage: { nodes: allPages = [] } = {},
    fishermanBusinessWebsitePage: { components: contentNodeComponents = [] } = {},
    allFishermanBusinessFile: { nodes: allFiles = [] },
    allFishermanBusinessTeam: { nodes: allTeamMembers = [] } = {},
    allFishermanBusinessLocation: { nodes: allLocations = [] } = {},
    site: {
      siteMetadata: { baseUrl },
    },
  } = data;
  const { fishermanParentPageTitle = 'Services', fishermanParentPageSlug = '/services/' } =
    pageContext;

  const {
    name: businessName,
    _id: businessId,
    type: businessType,
    businessCategories = [],
    website,
  } = business;

  const {
    _id,
    name,
    description,
    fullDescription,
    variations,
    files,
    available,
    interactions = [],
    annotations: { likeCount = 0 } = {},
    seoTitle,
    seoDescription,
    seoOgTitle,
    seoOgDescription,
    seoOgImage,
    duration,
    durationUnit,
    extraData: { attributes: itemAttributes = [] } = {},
  } = fishermanBusinessMenuItem;

  const url = getPageUrl(website, [
    createServiceItemSlug(fishermanBusinessMenuItem, allItems, categories),
  ]);

  function getItemCategory() {
    return categories.find(({ items }) => {
      return items.includes(_id);
    });
  }

  function getItemFiles() {
    return getBusinessFiles(files, allFiles);
  }

  const itemCategory = getItemCategory();
  const bookingPage = getBookNowPage(allPages);
  const otherCategoryItems = getOtherCategoryItems(_id, itemCategory, allItems);
  const itemFiles = getItemFiles();
  const itemModifierSets = getServiceItemModifierSets(fishermanBusinessMenuItem, allModifierSets);
  const filteredInteractions = getCtaInteractions(interactions);
  const { name: categoryName } = itemCategory || {};
  const hasSingleVariation = variations.length === 1;
  const multipleVariations = !hasSingleVariation;
  const displayPriceDetailsSection = multipleVariations || !_isEmpty(itemModifierSets);
  const teamMembers = getServiceTeamMembers(fishermanBusinessMenuItem, allTeamMembers);

  const priceDetailsHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'service_item_pricing_section_header',
  });
  const priceDetailsDescriptionContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'service_item_pricing_section_description',
  });
  const variationsHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'service_item_pricing_options_header',
  });
  const modifiersHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'service_item_pricing_add_ons_header',
  });
  const otherServiceItemsHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'service_item_other_services_header',
  });
  const otherServiceItemsSubheaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'service_item_other_services_subheader',
  });
  const otherServiceItemsDescriptionContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'service_item_other_services_description',
  });
  const seeAllServicesButtonContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'service_item_see_all_services_button',
  });

  return (
    <>
      <SEO
        businessName={businessName}
        pageName={name}
        title={seoTitle}
        description={seoDescription || description}
        truncateDescription
        images={itemFiles}
        ogTitle={seoOgTitle}
        ogDescription={seoOgDescription}
        ogImage={seoOgImage}
        ogType="product"
        ogUrl={url}
      />
      <ServiceItemSchema
        businessName={businessName}
        businessCategories={businessCategories}
        name={name}
        description={description}
        categoryName={categoryName}
        duration={duration}
        durationUnit={durationUnit}
        prices={variations.map((variation) => variation.price)}
        image={_head(itemFiles)}
      />
      <BreadcrumbListSchema
        items={[
          {
            name: `${fishermanParentPageTitle} | ${businessName}`,
            url: constructFullPageUrl(baseUrl, fishermanParentPageSlug),
          },
          {
            name: `Services - ${categoryName} | ${businessName}`,
            url: constructFullPageUrl(baseUrl, createServiceCategorySlug(itemCategory, categories)),
          },
          { name: `${name} | ${businessName}` },
        ]}
      />
      <div className="service-item-page">
        <Section className="info-section">
          <Breadcrumb>
            <Breadcrumb.Section
              link
              to={ensureSlugSlashes(fishermanParentPageSlug)}
              as={InternalLink}
            >
              {fishermanParentPageTitle}
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon="right chevron" />
            <Breadcrumb.Section
              link
              to={createServiceCategorySlug(itemCategory, categories)}
              as={InternalLink}
            >
              {categoryName}
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon="right chevron" />
            <Breadcrumb.Section>{name}</Breadcrumb.Section>
          </Breadcrumb>

          <Grid stackable>
            {!_isEmpty(itemFiles) && (
              <Grid.Column width={8} className="image-column">
                <FadeReveal triggerOnce>
                  <BasicCarousel
                    size={1}
                    grouping={1}
                    displayIndicators={false}
                    displayPlayPauseButton={false}
                    autoScroll={false}
                    slide
                    items={componentIterator({
                      iterator: itemFiles,
                      component: <Image background loadStrategy="eager" />,
                      propMap: { src: 'src', altText: 'altText' },
                    })}
                  />
                </FadeReveal>
              </Grid.Column>
            )}
            <Grid.Column width={8} className="information-column">
              <Header as="h1">{name}</Header>
              <div className="item-actions">
                <CatalogItemLikeButton
                  businessId={businessId}
                  businessType={businessType}
                  catalogItemId={_id}
                  initialLikeCount={likeCount}
                  event={{
                    category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                    action: EVENT_ACTIONS.SERVICES_LIKE_ITEM_EVENT_ACTION,
                    label: name,
                  }}
                />
                <CatalogItemShareButton
                  url={constructFullPageUrl(
                    baseUrl,
                    createServiceItemSlug(fishermanBusinessMenuItem, allItems, categories),
                  )}
                  event={{
                    category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                    action: EVENT_ACTIONS.SERVICES_SHARE_ITEM_EVENT_ACTION,
                    label: name,
                  }}
                />
              </div>
              {hasSingleVariation && <Price amount={variations[0].price} displayZeroCost={false} />}
              {!available && <Label className="unavailable">Unavailable</Label>}
              <MarkdownContent content={description} className="short-description" />
              <ServiceItemDuration
                className="attribute-information"
                duration={duration}
                durationUnit={durationUnit}
              />
              {!_isEmpty(teamMembers) && (
                <TeamMemberLinkList
                  className="attribute-information"
                  teamMembers={teamMembers}
                  allTeamMembers={allTeamMembers}
                />
              )}
              <ItemLocationAvailability
                className="attribute-information"
                item={fishermanBusinessMenuItem}
                locations={allLocations}
              />
              <div className="ctas-container">
                {bookingPage && _isEmpty(filteredInteractions) && (
                  <Button
                    secondary
                    as={OutboundLink}
                    to={bookingPage.url}
                    event={{
                      category: EVENT_CATEGORIES.BOOKING_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.BOOKING_SCHEDULE_APPOINTMENT_INTENT_EVENT_ACTION,
                    }}
                  >
                    {bookingPage.title}
                  </Button>
                )}
                {filteredInteractions.map((interaction) => {
                  return (
                    <WebsiteComponentInteraction
                      key={interaction._id}
                      interaction={interaction}
                      displayComponentPropsMap={{
                        button: {
                          size: null,
                          compact: false,
                        },
                      }}
                      event={{
                        category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                        action: EVENT_ACTIONS.SERVICES_ITEM_INTERACTION_ACTION,
                      }}
                    />
                  );
                })}
              </div>

              {fullDescription && (
                <MarkdownContent content={fullDescription} className="full-description" />
              )}
              <ServiceItemAttributeList attributes={itemAttributes} />
            </Grid.Column>
          </Grid>
        </Section>

        {displayPriceDetailsSection && (
          <Section className="price-details-section">
            {priceDetailsHeaderContent && <Header as="h2">{priceDetailsHeaderContent}</Header>}
            {priceDetailsDescriptionContent && (
              <MarkdownContent content={priceDetailsDescriptionContent} />
            )}
            <Grid stackable>
              {!hasSingleVariation && (
                <Grid.Column width={8} className="variations-column">
                  <Header as="h3">{variationsHeaderContent}</Header>
                  <List className="variations-container">
                    {variations.map((variation) => {
                      const {
                        _id: variationId,
                        name: variationName,
                        price: variationPrice,
                      } = variation;

                      const extraData = createVariationShortDescription(variation);

                      return (
                        <List.Item key={variationId}>
                          <List.Content>
                            <div className="header-container">
                              <List.Header>{variationName}</List.Header>
                              <Price amount={variationPrice} displayZeroCost={false} />
                            </div>
                            {!_isEmpty(extraData) && (
                              <div className="extra-data-container">
                                <MarkdownContent content={extraData} />
                              </div>
                            )}
                          </List.Content>
                        </List.Item>
                      );
                    })}
                  </List>
                </Grid.Column>
              )}
              {!_isEmpty(itemModifierSets) && (
                <Grid.Column width={8} className="modifiers-column">
                  <Header as="h3">{modifiersHeaderContent}</Header>
                  <ModifierSetsAccordions
                    modifierSets={itemModifierSets}
                    modifiers={allModifiers}
                    displayDescription
                    displayMinMaxAllowed
                  />
                </Grid.Column>
              )}
            </Grid>
          </Section>
        )}

        {!_isEmpty(otherCategoryItems) && (
          <Section className="related-service-items-section">
            <ScrollableContent direction="horizontal" distanceFactor="snap" intervalFactor={500}>
              <Grid stackable columns={2}>
                <Grid.Column className="content-column" width={4}>
                  {otherServiceItemsHeaderContent && (
                    <Header as="h2" content={otherServiceItemsHeaderContent} />
                  )}
                  {otherServiceItemsSubheaderContent && (
                    <Header as="h3" content={otherServiceItemsSubheaderContent} />
                  )}
                  {otherServiceItemsDescriptionContent && (
                    <MarkdownContent content={otherServiceItemsDescriptionContent} />
                  )}
                  <InternalLink
                    to={ensureSlugSlashes(fishermanParentPageSlug)}
                    label="See All Services"
                    event={{
                      category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.SERVICES_VIEW_ALL_SERVICES_EVENT_ACTION,
                    }}
                    className="ui button primary see-more-button"
                  >
                    {seeAllServicesButtonContent || 'See All Services'}
                  </InternalLink>
                  <div className="triggers-container">
                    <ScrollableContent.PreviousTrigger />
                    <ScrollableContent.NextTrigger />
                  </div>
                </Grid.Column>
                <Grid.Column className="cards-column" width={12}>
                  <ScrollableContent.Content hideScrollbar>
                    {otherCategoryItems.map((item) => {
                      const {
                        _id: itemId,
                        name: itemName,
                        description: itemDescription,
                        files: otherItemImages,
                      } = item;

                      const otherItemFile = getFirstBusinessFile(otherItemImages, allFiles);
                      const { src: otherItemFileSrc, altText: otherItemFileAlt } =
                        otherItemFile || {};

                      return (
                        <FadeReveal key={itemId} triggerOnce className="ui card">
                          <FullImageCard
                            as={InternalLink}
                            to={createServiceItemSlug(item, allItems, categories)}
                            link
                            image={otherItemFileSrc}
                            altText={otherItemFileAlt}
                            event={{
                              category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                              action: EVENT_ACTIONS.SERVICES_VIEW_CATEGORY_DETAILS_EVENT_ACTION,
                              label: itemName,
                            }}
                            className="item-card"
                          >
                            <Card.Header>
                              <span>{itemName}</span>
                              <FishermanIcon iconName="arrow-right" />
                            </Card.Header>
                            <MarkdownContent className="description" content={itemDescription} />
                          </FullImageCard>
                        </FadeReveal>
                      );
                    })}
                  </ScrollableContent.Content>
                  <div className="actions-container">
                    <InternalLink
                      to={ensureSlugSlashes(fishermanParentPageSlug)}
                      label="See All Services"
                      event={{
                        category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                        action: EVENT_ACTIONS.SERVICES_VIEW_ALL_SERVICES_EVENT_ACTION,
                      }}
                      className="ui button primary see-more-button"
                    >
                      {seeAllServicesButtonContent || 'See All Services'}
                    </InternalLink>
                    <div className="triggers-container">
                      <ScrollableContent.PreviousTrigger />
                      <ScrollableContent.NextTrigger />
                    </div>
                  </div>
                </Grid.Column>
              </Grid>
            </ScrollableContent>
          </Section>
        )}
      </div>
    </>
  );
}

export const query = graphql`
  query ($id: String!) {
    site {
      siteMetadata {
        baseUrl
      }
    }
    fishermanBusiness {
      _id
      type
      name
      businessCategories {
        category
      }
      website {
        domains {
          url
          primaryDomain
        }
      }
    }
    allFishermanBusinessWebsitePage {
      nodes {
        url
        title
        pageType
      }
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Services" }) {
      components {
        componentIdentifier
        contentNodes {
          content
        }
      }
    }
    allFishermanBusinessMenuCategory {
      nodes {
        _id
        items
        name
        files
      }
    }
    allFishermanBusinessMenuItem(sort: { order: ASC, fields: order }) {
      nodes {
        description
        _id
        name
        variations {
          _id
          menuItem
          name
          order
          price
        }
        annotations {
          likeCount
        }
        visible
        available
        files
        modifierSets
      }
    }
    allFishermanBusinessMenuModifierSet(sort: { order: ASC, fields: order }) {
      nodes {
        _id
        name
        order
        modifiers
        minAllowed
        maxAllowed
        description
      }
    }
    allFishermanBusinessMenuModifier(sort: { order: ASC, fields: order }) {
      nodes {
        _id
        name
        description
        order
        price
      }
    }
    fishermanBusinessMenuItem(_id: { eq: $id }) {
      _id
      name
      description
      fullDescription
      files
      interactions {
        _id
        displayType
        behaviorType
        url
        enabled
        style
        label
      }
      variations {
        _id
        menuItem
        name
        order
        price
        shortDescription
        duration
        durationUnit
      }
      annotations {
        likeCount
      }
      visible
      available
      modifierSets
      seoTitle
      seoDescription
      seoOgTitle
      seoOgDescription
      seoOgImage {
        url
        alt
        type
      }
      duration
      durationUnit
      extraData {
        attributes {
          description
          icon
          iconLibrary
          label
          type
        }
      }
      locations {
        location
      }
    }
    allFishermanBusinessFile {
      nodes {
        _id
        file
        gatsbyFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        description
      }
    }
    allFishermanBusinessTeam(sort: { order: ASC, fields: order }, filter: { show: { eq: true } }) {
      nodes {
        _id
        name
        show
        associatedItemVariations {
          itemId
          variationId
          item {
            _id
            name
            variations {
              _id
              name
            }
            visible
          }
        }
      }
    }
    allFishermanBusinessLocation {
      nodes {
        name
        city
        street
        _id
      }
    }
  }
`;
